import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Checkbox, Flex } from 'antd';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  DeleteFormTableRowButton,
  FormDateRangePicker,
  FormField,
  FormSelect,
  TanstackFormTable,
  getFormTableFieldName,
} from '../../common/form';
import { compareIsoDates, formatDate } from '../../common/utils';
import { ExecutionCalendarFormDataType } from '../schema';
import translations from '../translations/ExecutionCalendar.translations.json';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { usePeriodLoadingType } from './utils';
import {
  getDatePickerMode,
  RequiredText,
  TextEllipsis,
} from '../../common/components';
import { useCalendarSelect } from './useCalendarSelect';
import { CalendarSelectModal } from './CalendarSelectModal';
import { useShowPastYear } from './useShowPastYear';
import {
  SystemClassifierOption,
  useSystemClassifierSelectOptions,
} from '../../configuration';
import { DataObjectGroupType, Frequency } from '../types';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'execution-calendar-edit'
);
addTranslations(translations);

const columnHelper = createColumnHelper<ExecutionCalendarFormDataType>();

export function ExecutionCalendar({
  groupType,
  isInput,
  frequency,
}: {
  groupType: DataObjectGroupType;
  isInput: boolean;
  frequency?: Frequency;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const { cloneLastYear, selected, setSelected, updateValues } =
    useCalendarSelect('execution.executionCalendar');
  const { periodLoadingType } = usePeriodLoadingType();
  const { SOURCE_DATA_VERSION: sourceDataVersionOptions } =
    useSystemClassifierSelectOptions(['SOURCE_DATA_VERSION']);
  const {
    showPastYear,
    onChange: onChangeShowPastYear,
    hideOldRows,
  } = useShowPastYear();

  const showModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleOk = useCallback(() => {
    setIsModalOpen(false);
    updateValues(selected);
  }, [updateValues, selected]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('executionDate', {
        header: t('column.executionDate.label'),
        meta: { width: 120 },
        enableColumnFilter: false,
        enableSorting: false,
        cell: ExecutionDateCell,
        sortingFn: (a, b) =>
          compareIsoDates(a.original.executionDate, b.original.executionDate),
      }),
      columnHelper.accessor('validPeriod', {
        header: () =>
          periodLoadingType === 'ALL' ? (
            t('column.validPeriod.label')
          ) : (
            <RequiredText>{t('column.validPeriod.label')}</RequiredText>
          ),
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => (
          <ValidPeriodCell
            {...props}
            periodLoadingType={periodLoadingType}
            frequency={frequency}
          />
        ),
      }),
      ...(groupType === DataObjectGroupType.O
        ? [
            columnHelper.accessor('sourceDataVersionId', {
              header: t('column.sourceDataVersionId.label'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: { width: 220 },
              cell: (props) => (
                <SourceDataVersionCell
                  {...props}
                  options={sourceDataVersionOptions}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor('operations' as any, {
        header: undefined,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <Flex justify="center" gap="small">
            <DeleteFormTableRowButton context={props} />
          </Flex>
        ),
        meta: {
          width: 40,
          className: 'sticky-right',
        },
      }),
    ],
    [frequency, groupType, periodLoadingType, sourceDataVersionOptions, t]
  );

  return (
    <CalendarTableWrapper>
      <ButtonRowWrapper justify="space-between" align="center" gap={12}>
        <Checkbox
          id="show-past-year"
          checked={showPastYear}
          onChange={onChangeShowPastYear}
        >
          {t('checkbox.show-past-year')}
        </Checkbox>
        <Flex gap="small">
          <Button id="clone-year" onClick={cloneLastYear}>
            {t('button.clone-year')}
          </Button>
          <Button id="manage-dates" type="primary" onClick={showModal}>
            {t('button.manage-dates')}
          </Button>
        </Flex>
      </ButtonRowWrapper>
      <TanstackFormTable
        name="execution.executionCalendar"
        columns={columns}
        rowHtmlAttributes={hideOldRows}
        sticky
        scrollContainerSelector="#detail-layout-main-content"
      />
      <CalendarSelectModal
        title={
          isInput ? t('modal.calendar.title.in') : t('modal.calendar.title.out')
        }
        isOpen={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        selected={selected}
        onSelect={setSelected}
      />
    </CalendarTableWrapper>
  );
}

const ExecutionDateCell: FC<CellContext<ExecutionCalendarFormDataType, any>> =
  memo((props) => (
    <FormField
      name={getFormTableFieldName(props)}
      size="small"
      preview={{
        previewOnly: true,
        render: (value) => formatDate(value),
      }}
    />
  ));

const ValidPeriodCell: FC<
  CellContext<ExecutionCalendarFormDataType, any> & {
    periodLoadingType?: string;
    frequency?: Frequency;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  return props.periodLoadingType === 'ALL' ? (
    <TextEllipsis>{t('column.validPeriod.ALL')}</TextEllipsis>
  ) : (
    <FormField name={name} size="small" style={{ maxWidth: '300px' }}>
      <FormDateRangePicker
        name={name}
        size="small"
        autoFocus
        allowEmpty={[false, false]}
        picker={getDatePickerMode(props.frequency)}
        endOfPeriod
      />
    </FormField>
  );
});

const SourceDataVersionCell: FC<
  CellContext<ExecutionCalendarFormDataType, any> & {
    options: SystemClassifierOption[];
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField size="small" name={name}>
      <FormSelect size="small" name={name} options={props.options} />
    </FormField>
  );
});

const ButtonRowWrapper = styled(Flex)`
  padding-bottom: 12px;
`;

const CalendarTableWrapper = styled.div`
  width: 100%;
`;
